import { API_ADDRESS, post, del, get } from 'lib/communication';
import store from 'store';
import { dataActions } from 'reducers';
import produce from 'immer';
import { Order, DetailedOrder, Product, ProductPrice, LocalCart, ProductsGroup, AddOrderRequestDTO, ImportedProduct, GutteringSystemData, AdditionalProduct, FoundProduct, OrderStatusNameToLabel, OrderStatusName, EditAssortmentImage, AddOrderResponse, UserDeliveryAddress, ProductInGroup, AppendOrderRequestDTO, OrderStatus, CartGroup, IncorrectImportedProduct, ProductInLocalCart } from 'lib/types';
import _ from 'lodash';
import { CartDTO, ImportedProductsInfoDTO, ProductInCartDTO, Response } from './communicationTypes';
import { getCurrentTimeStamp, isSafari } from 'lib/util';
import { GA_addProductsToCart, GA_createOrder, GA_orderModified, GA_orderRefunded, CartSource } from 'lib/gtm-utils';
import { userService } from 'services';

const setIsImageEdit = (isFetching: boolean) => {
    store.dispatch(dataActions.setIsEditImageUploadFetching(isFetching));
};

const setAreImagesEdit = (isFetching: boolean) => {
    store.dispatch(dataActions.setIsEditImagesUploadFetching(isFetching));
};

const setAreGraphicsDownloaded = (isFetching: boolean) => {
    store.dispatch(dataActions.setIsAssortmentGraphicsFetching(isFetching));
};

const setIsProductDetailsFetching = (isFetching: boolean) => {
    store.dispatch(dataActions.setIsProductDetailsFetching(isFetching));
};

const setIsAdditionalAssortmentDetailsFetching = (isFetching: boolean) => {
    store.dispatch(dataActions.setAdditionalAssortmentDetailsFetching(isFetching));
};

const setOrdersFetching = async (isFetching: object) => {
    const state = store.getState();
    store.dispatch(
        dataActions.setOrders(
            produce(state.data.orders, (draft) => {
                draft.props = {
                    ...draft.props,
                    ...isFetching
                };
            })
        )
    );
};

const toggleProductsOfGroupsFetching = async (path: string, set: boolean) => {
    const { productsOfGroupsFetching } = store.getState().data.orders.props;
    const newProds = [...productsOfGroupsFetching];

    if (set && productsOfGroupsFetching.indexOf(path) === -1) {
        newProds.push(path);
    }

    if (!set && productsOfGroupsFetching.indexOf(path) > -1) {
        newProds.splice(newProds.indexOf(path), 1);
    }

    store.dispatch(dataActions.setProductsOfGroupsFetching(newProds));
};

export const getGroups = async (): Promise<boolean> => {
    const route: string = 'products/getGroups';
    setOrdersFetching({ isFetchingGroups: true });
    try {
        const user = userService.getUser();
        if (user === null) return false;
        const { company, mag } = user;
        const data = await get<ProductsGroup[]>(`${API_ADDRESS}${route}?company=${company}&mag=${mag}`);
        if (data.ok && data.parsedBody) {
            const groups = data.parsedBody;
            store.dispatch(dataActions.setGroups(groups));
            return true;
        }
        return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setOrdersFetching({ isFetchingGroups: false });
    }
};

export const getProductsByGroup = async (group: string, company: string | undefined, signal?: AbortSignal): Promise<boolean> => {
    const route: string = 'products/getProducts';
    setOrdersFetching({ isFetchingGroups: true });
    toggleProductsOfGroupsFetching(group, true);
    try {
        const data = await post<ProductInGroup[]>(`${API_ADDRESS}${route}`, {
            company,
            group
        }, { signal });
        if (data.ok && data.parsedBody) {
            const fetchedProducts = data.parsedBody;
            store.dispatch(dataActions.addProductsInGroup(fetchedProducts));
            return true;
        } return false;
    } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
            return false;
        }
        console.log('Error ', err);
        return false;
    } finally {
        setOrdersFetching({ isFetchingGroups: false });
        toggleProductsOfGroupsFetching(group, false);
    }
};

export const getProductsByGroupReturnProducts = async (group: string, company: string | undefined): Promise<ProductInGroup[] | false> => {
    const route: string = 'products/getProducts';
    try {
        const data = await post<ProductInGroup[]>(`${API_ADDRESS}${route}`, {
            company,
            group
        });
        if (data.ok && data.parsedBody) {
            const fetchedProducts = data.parsedBody;
            store.dispatch(dataActions.addProductsInGroup(fetchedProducts));

            return fetchedProducts;
        } return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    }
};

const fetchImportedProductsInfo = async (products: { symKar: string; unit: string }[]): Promise<ImportedProductsInfoDTO> => {
    const route: string = 'products/getProductsPrice';

    const user = userService.getUser();
    if (user === null) throw new Error('User not found');
    const { login, logo } = user;
    const data = await post<ImportedProductsInfoDTO>(`${API_ADDRESS}${route}`, {
        login,
        logo,
        products
    });
    if (data.ok && data.parsedBody) {
        return data.parsedBody;
    }

    throw new Error('Fetching products info failed');
};

export const getProductWithPrice = async (products: { symKar: string; unit: string }[], isAdditionalAssortmentInfoFetching: boolean = false): Promise<boolean> => {
    if (!isAdditionalAssortmentInfoFetching) setIsProductDetailsFetching(true);
    else setIsAdditionalAssortmentDetailsFetching(true);
    try {
        const { productsAvailable } = await fetchImportedProductsInfo(products);
        store.dispatch(dataActions.addProducts(productsAvailable));
        return true;
    } catch (err) {
        console.log('Error', err);
        return false;
    } finally {
        if (!isAdditionalAssortmentInfoFetching) setIsProductDetailsFetching(false);
        else setIsAdditionalAssortmentDetailsFetching(false);
    }
};

export const searchProducts = async (description: string): Promise<FoundProduct[] | false> => {
    const route: string = 'products/searchProducts';
    const searchPhrase: string = description.replace(/\s/g, '%');
    setOrdersFetching({ isSearchingProducts: true });
    try {
        const user = userService.getUser();
        if (user === null) return false;
        const { login, logo, company, mag } = user;
        const { lang } = store.getState().conf;
        const data = await post<FoundProduct[]>(`${API_ADDRESS}${route}`, {
            login,
            logo,
            company,
            mag,
            lang,
            searchPhrase
        });
        if (data.ok && data.parsedBody) {
            const fetchedProducts = data.parsedBody;
            return fetchedProducts;
        } return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setOrdersFetching({ isSearchingProducts: false });
    }
};

export const getProductsInfo = async (products: { symKar: string, unit: string }[], keepInStore: boolean): Promise<Product[] | false> => {
    setIsProductDetailsFetching(true);
    try {
        const { productsAvailable } = await fetchImportedProductsInfo(products);
        if (keepInStore) {
            store.dispatch(dataActions.addProducts(productsAvailable));
        }
        return productsAvailable;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setIsProductDetailsFetching(false);
    }
};

export const getOrdersList = async (
    status: OrderStatusName,
    orderNumber: string,
    customerOrderNumber: string,
    dateFrom: string,
    dateTo: string,
    description: string
): Promise<boolean> => {
    const route: string = 'orders';
    setOrdersFetching({ isFetchingOrders: true });
    try {
        const user = userService.getUser();
        if (user === null) return false;
        const { logo, company } = user;
        const data = await post<Order[]>(`${API_ADDRESS}${route}`, {
            logo,
            company,
            status: OrderStatusNameToLabel[status],
            orderNumber,
            customerOrderNumber,
            dateFrom,
            dateTo,
            description
        });
        if (data.ok && data.parsedBody) {
            if (status === 'unauthorized') store.dispatch(dataActions.setOrdersList({ status, data: data.parsedBody?.filter((order) => order.authorizationStatus === 'Brak') || [] }));
            else store.dispatch(dataActions.setOrdersList({ status, data: data.parsedBody }));
            return true;
        } return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setOrdersFetching({ isFetchingOrders: false });
    }
};

const cartGroupsInOrder = (): CartGroup[] => _.map(
    store.getState().data.orders.localCart.productsInLocalCart,
    (product) => product.cartGroup
);

export const addOrder = async (detailedOrder: DetailedOrder, summaryPrice: number): Promise<boolean> => {
    const route: string = 'orders/createNewOrder';
    setOrdersFetching({ isAddingOrder: true });
    try {
        const user = userService.getUser();
        if (user === null) return false;
        const { login, logo } = user;
        const addOrderRequestDTO: AddOrderRequestDTO = {
            login,
            logo,
            description: detailedOrder.description,
            customerDesc: detailedOrder.customerDesc,
            customerOrderNumber: detailedOrder.customerOrderNumber,
            address: detailedOrder.address?.id as string || '0',
            contact: detailedOrder.contact || '',
            phoneNumber: detailedOrder.phoneNumber,
            roofer: detailedOrder.rooferLogin,
            addToExisting: detailedOrder.addToExisting,
            products: detailedOrder.products.map((product) => ({
                symKar: product.product.symKar,
                quantity: product.quantity,
                unit: product.product.unit,
                dimension: product.dimension ?? undefined,
                additionsSelected: product.additionsSelected !== null ?
                    product.additionsSelected.map((isSelected, index) => {
                        if (isSelected) {
                            return product.product.additions[index];
                        }
                        return null;
                    }) : [null]
            }))
        };

        const data = await post<AddOrderResponse>(`${API_ADDRESS}${route}`, addOrderRequestDTO);
        if (data.ok && data.parsedBody) {
            if ('error' in data.parsedBody) return false;
            GA_createOrder(data.parsedBody.NagId, detailedOrder.products, summaryPrice, cartGroupsInOrder());
            store.dispatch(dataActions.clearLocalCart());
            store.dispatch(dataActions.clearCart());
            return true;
        }
        return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setOrdersFetching({ isAddingOrder: false });
    }
};

export const appendOrder = async (detailedOrder: DetailedOrder, nagIdSource: string, summaryPrice: number): Promise<boolean> => {
    const route: string = 'orders/createNewOrder';
    setOrdersFetching({ isAddingOrder: true });
    try {
        const user = userService.getUser();
        if (user === null) return false;
        const { login, logo } = user;
        const appendOrderRequestDTO: AppendOrderRequestDTO = {
            login,
            logo,
            description: detailedOrder.description,
            customerDesc: detailedOrder.customerDesc,
            customerOrderNumber: detailedOrder.customerOrderNumber,
            address: detailedOrder.address?.id as string || '0',
            contact: detailedOrder.contact || '',
            phoneNumber: detailedOrder.phoneNumber,
            roofer: detailedOrder.rooferLogin,
            addToExisting: detailedOrder.addToExisting,
            products: detailedOrder.products.map((product) => ({
                symKar: product.product.symKar,
                quantity: product.quantity,
                unit: product.product.unit,
                dimension: product.dimension ?? undefined,
                additionsSelected: product.additionsSelected !== null ?
                    product.additionsSelected.map((isSelected, index) => {
                        if (isSelected) {
                            return product.product.additions[index];
                        }
                        return null;
                    }) : [null]
            })),
            nagIdSource
        };

        const data = await post<AddOrderResponse>(`${API_ADDRESS}${route}`, appendOrderRequestDTO);
        if (data.ok && data.parsedBody) {
            GA_createOrder(data.parsedBody.NagId, detailedOrder.products, summaryPrice, cartGroupsInOrder());
            store.dispatch(dataActions.clearLocalCart());
            store.dispatch(dataActions.clearCart());
            return true;
        }
        return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setOrdersFetching({ isAddingOrder: false });
    }
};

export const deleteOrder = async (orderNumber: string, orderValue: number): Promise<boolean> => {
    const route: string = 'orders/delete';
    setOrdersFetching({ pdfIsFetching: true });
    try {
        const user = userService.getUser();
        if (user === null) return false;
        const { logo } = user;
        const data = await del<Response>(`${API_ADDRESS}${route}`, {
            orderId: orderNumber,
            logo
        });
        if (data.ok) {
            GA_orderRefunded(orderNumber, orderValue);
            const state = store.getState();
            _.forEach(state.data.orders.orders, (orders, key) => {
                if (orders !== null) {
                    const filtered: Order[] = _.filter(orders, (o) => o.id !== orderNumber);
                    if (orders.length !== filtered.length) {
                        store.dispatch(dataActions.setOrdersList({ status: key as OrderStatusName, data: filtered }));
                    }
                }
            });
            return true;
        } return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setOrdersFetching({ pdfIsFetching: false });
    }
};

export const modifyOrder = async (detailedOrder: DetailedOrder): Promise<boolean> => {
    const route: string = 'orders/modify';
    setOrdersFetching({ isAddingOrder: true });
    try {
        const user = userService.getUser();
        if (user === null) return false;
        const { login, logo } = user;

        const addOrderRequestDTO: AddOrderRequestDTO = {
            login,
            logo,
            orderId: detailedOrder.orderId,
            customerOrderNumber: detailedOrder.customerOrderNumber,
            description: detailedOrder.description,
            contact: detailedOrder.contact || '',
            phoneNumber: detailedOrder.phoneNumber,
            customerDesc: detailedOrder.customerDesc,
            address: detailedOrder.address?.id as string || '0',
            roofer: detailedOrder.rooferLogin,
            addToExisting: detailedOrder.addToExisting,
            products: detailedOrder.products.map((product) => ({
                symKar: product.product.symKar,
                quantity: product.quantity,
                unit: product.product.unit,
                dimension: product.dimension ?? undefined,
                additionsSelected: product.additionsSelected !== null ?
                    product.additionsSelected.map((isSelected, index) => {
                        if (isSelected) {
                            return product.product.additions[index];
                        }
                        return null;
                    }) : [null]
            }))
        };
        const data = await post<Response>(`${API_ADDRESS}${route}`, addOrderRequestDTO);
        if (data.ok && data.parsedBody && !('error' in data.parsedBody)) {
            GA_orderModified(detailedOrder.orderId, detailedOrder.products, cartGroupsInOrder());
            store.dispatch(
                dataActions.clearLocalCart()
            );
            return true;
        }
        return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setOrdersFetching({ isAddingOrder: false });
    }
};

export const changeOrderAuthorization = async (orderId: string, statusFrom: string, statusTo: string): Promise<boolean> => {
    const route: string = 'orders/authorize';
    setOrdersFetching({ pdfIsFetching: true });
    try {
        const user = userService.getUser();
        if (user === null) return false;
        const { login, logo } = user;
        const data = await post<Response>(`${API_ADDRESS}${route}`, {
            orderId,
            logo,
            login,
            statusFrom,
            statusTo
        });
        if (data.ok && data.parsedBody) {
            if (typeof data.parsedBody === 'object' && 'error' in data.parsedBody) return false;
            return true;
        } return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setOrdersFetching({ pdfIsFetching: false });
    }
};

export const getOrderById = async (
    orderId: string,
    replaceLocalCart: boolean,
    source: CartSource | null
): Promise<DetailedOrder | false> => {
    const user = userService.getUser();
    if (user === null) return false;
    const { login, logo } = user;
    const route: string = 'orders/getOrder';
    setOrdersFetching({ isFetching: true });

    try {
        const data = await post<DetailedOrder>(`${API_ADDRESS}${route}`, {
            logo,
            login,
            orderId
        });
        if (data.ok && data.parsedBody && !('error' in data.parsedBody)) {
            const order = data.parsedBody;

            const detailedOrder: DetailedOrder = {
                orderId: order.orderId,
                orderNumber: order.orderNumber,
                customerOrderNumber: order.customerOrderNumber,
                description: order.description,
                customerDesc: order.customerDesc,
                status: order.status,
                address: new UserDeliveryAddress(order.address),
                contact: order.contact,
                phoneNumber: order.phoneNumber,
                addToExisting: order.addToExisting,
                products: order.products,
                rooferLogin: order.rooferLogin
            };

            if (order.productsNotAvailable === true) {
                store.dispatch(dataActions.setCartProdNotAvailable(true));
            }

            if (replaceLocalCart) {
                store.dispatch(dataActions.clearLocalCart());

                const addedToCart: ProductInLocalCart[] = _.map(detailedOrder.products, (productInOrder) => ({
                    dimension: productInOrder.dimension,
                    quantity: productInOrder.quantity,
                    additionsSelected: productInOrder.additionsSelected,
                    product: productInOrder.product,
                    mainProductSymkar: null,
                    unit: productInOrder.product.priceInfo.units.find((u) => u.unit === productInOrder.unit)!,
                    cartGroup: CartGroup.main,
                    systemData: null
                }));

                _.forEach(addedToCart, (product) => {
                    store.dispatch(dataActions.addToLocalCart({ product }));
                });

                if (source !== null && addedToCart.length > 0) {
                    GA_addProductsToCart(addedToCart, source);
                }
            }
            return detailedOrder;
        } return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setOrdersFetching({ isFetching: false });
    }
};

export const getOrderPdf = async (orderId: string, orderNet: number, orderVat: number, orderGross: number): Promise<boolean> => {
    const route: string = 'orders/pdf';
    const user = userService.getUser();
    if (user === null) return false;
    const { login, logo, company, wydr } = user;
    let windowReference: Window | null = null;
    if (isSafari()) {
        windowReference = window.open();
    }

    setOrdersFetching({ pdfIsFetching: true });
    try {
        const res = await post<any>(`${API_ADDRESS}${route}`, {
            logo,
            login,
            company,
            orderId,
            wydr,
            orderNet,
            orderVat,
            orderGross
        });
        if (res.ok) {
            const blob = await res.blob();
            const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
            if (isSafari()) {
                if (windowReference !== null) windowReference.location.href = url;
            } else {
                window.open(url);
            }
            return true;
        } return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setOrdersFetching({ pdfIsFetching: false });
    }
};

export const getProductPriceInfo = async (product: Product, index?: number): Promise<boolean> => {
    const route: string = 'products/getProductPrice';
    try {
        const user = userService.getUser();
        if (user === null) return false;
        const { login, logo } = user;
        const data = await post<ProductPrice>(`${API_ADDRESS}${route}`, {
            login,
            logo,
            unit: product.unit,
            symKar: product.symKar
        });
        if (data.ok && data.parsedBody) {
            if (index !== undefined) {
                store.dispatch(
                    dataActions.setAdditionalAssortmentPrice({ productPrice: data.parsedBody, index })
                );
            }
            return true;
        } return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setOrdersFetching({ isFetching: false });
    }
};

export const getAdditionalAssortmentInfo = async (products: AdditionalProduct[]): Promise<boolean> => {
    try {
        const { productsAvailable } = await fetchImportedProductsInfo(products);
        store.dispatch(dataActions.addProducts(productsAvailable));
        return true;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setOrdersFetching({ isFetching: false });
    }
};

export const getImportedProductsInfo = async (products: ImportedProduct[], mergeCart?: boolean): Promise<boolean> => {
    setOrdersFetching({ isFetching: true });
    const importsWithoutProperUnit: IncorrectImportedProduct[] = [];
    const addedToCart: ProductInLocalCart[] = [];
    try {
        const { productsAvailable, productsNotAvailable } = await fetchImportedProductsInfo(products);
        if (productsNotAvailable.length > 0) {
            store.dispatch(dataActions.setCartProdNotAvailable(true));
        }
        _.map(products, (product: ImportedProduct) => {
            const foundProduct: Product | undefined = _.find(productsAvailable, (p) => p.symKar === product.symKar);
            if (foundProduct === undefined) return;
            if (foundProduct.priceInfo.units.find((u) => u.unit === product.unit) === undefined) {
                importsWithoutProperUnit.push({ quantity: product.unit.toLowerCase() === 'szt' ? Math.ceil(product.quantity) : product.quantity, description: foundProduct.description, unit: product.unit } as IncorrectImportedProduct);
                return;
            }
            const productInLocalCart: ProductInLocalCart = {
                product: foundProduct,
                quantity: product.quantity,
                dimension: (foundProduct.isDimension || product.dimension === null) ? (foundProduct.dimensionCanEdit ? product.dimension : foundProduct.dimensionDefault) : 1.000,
                additionsSelected: foundProduct.additions.map((add) => {
                    if (product.additionsSelected.includes(add.parId)) return true;
                    return false;
                }),
                systemData: product.systemData,
                cartGroup: product.cartGroup,
                unit: foundProduct.priceInfo.units.find((u) => u.unit === product.unit)!,
                mainProductSymkar: null
            };
            addedToCart.push(productInLocalCart);
            store.dispatch(dataActions.addToLocalCart({ product: productInLocalCart }));
        });

        if (addedToCart.length > 0) {
            GA_addProductsToCart(
                addedToCart,
                addedToCart[0].cartGroup === CartGroup.isod ? 'isod' : 'gutter_creator',
                importsWithoutProperUnit.length,
                mergeCart
            );
        }
        return true;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setOrdersFetching({ isFetching: false });
        store.dispatch(dataActions.setImportsWithoutProperUnit(importsWithoutProperUnit));
    }
};

// export const getImportedProductsInfoMocked = async (products: ImportedProduct[], edit: boolean): Promise<boolean> => {
//     setOrdersFetching({ isFetching: true });
//     try {
//         const prod = products[0];
//         const pathValue = [`plyty/${prod.symKar}`];
//         const responseMocked = {
//             symKar: prod.symKar,
//             path: pathValue,
//             description: 'plyty wartswowe',
//             descriptionEN: 'plyty warstwowe',
//             descriptionRU: 'plyty warstwowe',
//             unit: String(prod.dimension),
//             isDimension: true,
//             dimensionDefault: prod.quantity,
//             remarks: '',
//             additions: [],
//             priceInfo: {
//                 price: 100,
//                 priceWithFoil: 11,
//                 discount: 1,
//                 discountWithFoil: 2,
//                 priceAfterDiscount: 98,
//                 priceWithFoilAfterDiscount: 96,
//                 vatRate: 23,
//                 vat: 22,
//                 vatWithFoil: 24,
//                 gross: 120,
//                 grossWithFoil: 122,
//                 converterM2: 1.3
//             },
//             additionalAssortment: null,
//             additionsSelected: [false]
//         };
//         const data = { parsedBody: responseMocked, ok: true };
//         if (data.ok && data.parsedBody) {
//             _.map(products, (product) => {
//                 store.dispatch(
//                     dataActions[reducer]({
//                         groupPath: `plyty`,
//                         productsType: 'mainProducts' as ProductsType,
//                         product: {
//                             product: data.parsedBody,
//                             quantity: product.quantity,
//                             dimension: product.dimension,
//                             additionsSelected: null
//                         }
//                     })
//                 );
//             });
//             return true;
//         }
//         return false;
//     } catch (err) {
//         console.log('Error ', err);
//         return false;
//     } finally {
//         setOrdersFetching({ isFetching: false });
//     }
// };

export const setSessionCart = async (localCart: LocalCart, summaryPrice: number): Promise<boolean> => {
    const route: string = 'cart/setCart';
    try {
        const cart: CartDTO = {
            products: localCart.productsInLocalCart.map((prd) => ({
                quantity: prd.quantity,
                dimension: prd.dimension,
                additionsSelected: prd.product.additions.map((add, index) => {
                    if (prd.additionsSelected?.[index]) {
                        return add.parId;
                    }

                    return null;
                }).filter(Boolean) as string[],
                symKar: prd.product.symKar,
                unit: prd.unit.unit,
                mainProductSymkar: prd.mainProductSymkar,
                systemData: prd.systemData,
                cartGroup: prd.cartGroup
            })),
            totalGrossValue: summaryPrice as number
        };
        const user = userService.getUser();
        if (user === null) return false;
        const { id, login } = user;
        const data = await post<Response>(`${API_ADDRESS}${route}`, {
            userId: id,
            login,
            localCart: JSON.stringify(cart)
        });
        if (data.ok && data.parsedBody) {
            return true;
        }
        return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    }
};

export const loadSessionCart = async (): Promise<boolean> => {
    const route: string = 'cart/loadCart';
    try {
        const user = userService.getUser();
        if (user === null) return false;
        const { id, login, logo } = user;
        const data = await post<{ cart: CartDTO, products: { [id: string]: Product }, cartProdNotAvailable: boolean }>(`${API_ADDRESS}${route}`, {
            userId: id,
            login,
            logo
        });
        if (data.ok && data.parsedBody) {
            const parsedData = data.parsedBody;
            const cart: LocalCart = {
                productsInLocalCart: parsedData.cart.products.map((prd: ProductInCartDTO) => ({
                    product: parsedData.products[prd.symKar],
                    quantity: prd.quantity,
                    dimension: prd.dimension,
                    additionsSelected: parsedData.products[prd.symKar].additions.map((add) => {
                        if (prd.additionsSelected.includes(add.parId)) return true;
                        return false;
                    }),
                    mainProductSymkar: prd.mainProductSymkar,
                    unit: parsedData.products[prd.symKar].priceInfo.units.find((u) => u.unit === prd.unit)!,
                    cartGroup: prd.cartGroup,
                    systemData: prd.systemData
                }))
            };
            store.dispatch(dataActions.addProducts(Object.values(parsedData.products)));
            store.dispatch(dataActions.setPreviousSummaryPrice(parsedData.cart.totalGrossValue));
            store.dispatch(dataActions.setCartProdNotAvailable(parsedData.cartProdNotAvailable));
            store.dispatch(
                dataActions.setLocalCart(cart)
            );
            return true;
        }
        return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    }
};

export const resetGutteringsCart = async (systemData: GutteringSystemData): Promise<boolean> => {
    store.dispatch(dataActions.clearLocalGutteringsCart({ gutteringSystemData: systemData }));
    return true;
};

const setIsOrderStatusFetching = (isFetching: boolean) => {
    store.dispatch(dataActions.setIsOrderStatusFetching(isFetching));
};

export const getOrderStatus = async (orderId: string) => {
    const route: string = 'orders/status';
    setIsOrderStatusFetching(true);
    try {
        const user = userService.getUser();
        if (user === null) return false;
        const { company, logo } = user;
        const data = await post<OrderStatus>(`${API_ADDRESS}${route}`, {
            company,
            logo,
            orderId
        });
        if (data.ok && data.parsedBody) {
            const orderStatus: OrderStatus = {
                ...data.parsedBody,
                header: {
                    ...data.parsedBody.header,
                    date: new Date(data.parsedBody.header.date)
                }
            };
            store.dispatch(dataActions.setOrderStatus(orderStatus));
            return true;
        } return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setIsOrderStatusFetching(false);
    }
};

export const resetIsodCart = () => {
    store.dispatch(dataActions.clearIsodCart());
};

export const editImage = async (file: File): Promise<EditAssortmentImage | boolean> => {
    const route: string = 'products/editProductOrGroupImage';
    try {
        setIsImageEdit(true);
        const formData = new FormData();
        formData.append('file', file);
        const data = await post<EditAssortmentImage>(`${API_ADDRESS}${route}`, formData);
        if (data.ok && data.parsedBody) return data.parsedBody;
        return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setIsImageEdit(false);
    }
};

export const editImages = async (file: File): Promise<EditAssortmentImage[] | boolean> => {
    const route: string = 'products/editProductOrGroupImages';
    try {
        setAreImagesEdit(true);
        const formData = new FormData();
        formData.append('file', file);
        const data = await post<EditAssortmentImage[]>(`${API_ADDRESS}${route}`, formData);
        if (data.ok && data.parsedBody) return data.parsedBody;
        return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setAreImagesEdit(false);
    }
};

export const downloadAssortmentGraphics = async (): Promise<boolean> => {
    const route: string = 'products/downloadAssortmentGraphics';
    try {
        setAreGraphicsDownloaded(true);
        const response = await get<any>(`${API_ADDRESS}${route}`);
        if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/zip' }));
            const a = document.createElement('a');
            document.body.append(a);
            a.href = url;
            a.download = `${getCurrentTimeStamp()}_zamówienia_grafiki.zip`;
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            return true;
        } return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setAreGraphicsDownloaded(false);
    }
};
